import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Button, Modal } from "@mui/material";
import { Button as Btn, Typography } from "@material-ui/core";
import { PrimaryColor } from "../../shared/styles/colors";
import Calendar from "react-calendar";
import CalendarService from "../../service/CalendarService";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DB_Status from "../../model/enum/DBStatus";
import CloseIcon from "@mui/icons-material/Close";
import AppointmentModal from "./AppointmentModal";
import SendEmailModal from "../modal/SendEmailModal";
import ZillowIcon from "../../assets/admin/zillow-icon.svg";
import ClientModel from "../../model/client";
import DateConverter from "../../utils/DateConverter";
import AppointmentModel from "../../model/appointment";
import UserModel from "../../model/user";
import StatusModel from "../../model/status";
import useStyles from "./styles";
import styled from "styled-components";
import UpdateStatusModal from "./UpdateStatusModal";

// Customized the calendar
export const CalendarWrapper = styled.div`
  .react-calendar__navigation__label,
  .react-calendar__navigation__arrow {
    background-color: white; /* Custom color */
    color: black; /* Button text color */
    border: none; /* Remove default border */
    padding: 5px 10px;
    border-radius: 20px;
    cursor: pointer;
  }

  .react-calendar__navigation__arrow:hover {
    background-color: #0056b3; /* Darker shade on hover */
  }

  .react-calendar__tile--active,
  .react-calendar__tile--active:after {
    color: white; /* Text color for active date */
    background: #1e2e55;
    border-radius: 20px; /* Optional: make the button rounded */
  }

  .react-calendar__tile--active:hover {
    background-color: #1e2e5580; /* Darker color on hover */
  }
`;

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "50rem",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  maxHeight: "90vh", // Restrict maximum height to 80% of the viewport height
  overflowY: "auto", // Enable vertical scrolling
  "@media (max-width: 640px)": {
    width: "88%",
  },
};

interface Props {
  appointment: AppointmentModel;
  resetList?: () => void;
  onOpen?: (id?: string) => void;
  onClose?: () => void;
}

const AppointmentDetailModal = ({
  appointment,
  resetList,
  onOpen,
  onClose,
}: Props) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const client: ClientModel | undefined = appointment.Client;
  const user: UserModel | undefined = appointment.User;
  const status: StatusModel | undefined = appointment.Status;

  const handleOpen = () => {
    setOpen(true);
    if (onOpen) {
      onOpen(appointment.id?.toString());
    }
  };

  const handleClose = () => {
    setOpen(false);
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    const appointmentId = searchParams.get("appointmentId");
    if (
      !open &&
      appointmentId &&
      appointmentId === appointment.id?.toString()
    ) {
      handleOpen();
    }
  }, [searchParams]);

  const allowedStatusesForCalendar = () => {
    switch (status?.name) {
      case DB_Status.ACCEPTED:
        return true;
      case DB_Status.SCHEDULE_START_JOB:
        return true;
      case DB_Status.ESTIMATE_APPROVED:
        return true;
      default:
        return false;
    }
  };

  return (
    <div>
      <Button onClick={handleOpen}>
        <MoreVertIcon sx={{ color: PrimaryColor }} />
      </Button>
      {open && (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          sx={{ overflowY: "auto" }}
        >
          <Box sx={style}>
            <div className={classes.headerDiv}>
              <Typography className={classes.title}>
                <div style={DB_Status.mapStatusDivColor(status?.name)}>
                  <div
                    style={DB_Status.mapStatusRoundedColor(status?.name, false)}
                  ></div>
                </div>
              </Typography>
              <div className={classes.headerSubDiv}>
                {resetList && (
                  <UpdateStatusModal
                    appointment={appointment}
                    resetList={resetList}
                  />
                )}
                {allowedStatusesForCalendar() && (
                  <CalendarService
                    payload={appointment}
                    client={client}
                    seller={user}
                    handleClose={handleClose}
                  />
                )}
                {resetList && (
                  <AppointmentModal
                    appointment={appointment}
                    resetList={resetList}
                  />
                )}
                <Button onClick={handleClose} sx={{ color: "black" }}>
                  <CloseIcon />
                </Button>
              </div>
            </div>
            <hr />
            <div className={classes.form}>
              <div className={classes.container}>
                <div className={classes.inputContainer}>
                  <div className={classes.inputDiv}>
                    <Typography className={classes.label}>Client</Typography>
                    <Typography className={classes.inputDetail}>
                      {client?.firstName} {client?.lastName}
                    </Typography>
                  </div>
                  <div className={classes.inputGroup}>
                    <div className={classes.inputDiv}>
                      <Typography className={classes.label}>
                        Starting time
                      </Typography>
                      <Typography className={classes.inputDetail}>
                        {DateConverter.convertTo12HourFormat(
                          appointment.initialTime
                        )}
                      </Typography>
                    </div>
                    <div className={classes.inputDiv}>
                      <Typography className={classes.label}>
                        Ending time
                      </Typography>
                      <Typography className={classes.inputDetail}>
                        {DateConverter.convertTo12HourFormat(
                          appointment.finalTime
                        )}
                      </Typography>
                    </div>
                  </div>
                  <div className={classes.inputDiv}>
                    <Typography className={classes.label}>Seller</Typography>
                    <Typography className={classes.inputDetail}>
                      {appointment?.User?.firstName}{" "}
                      {appointment?.User?.lastName}
                    </Typography>
                  </div>
                  <Typography className={classes.label}>Notes</Typography>
                  <textarea
                    className={classes.textareaDetail}
                    value={appointment.notes}
                    disabled
                  />
                </div>
                <div
                  className={`calendar-container ${classes.calendarContainer}`}
                >
                  <Typography className={classes.label}>
                    Scheduled At
                  </Typography>
                  <CalendarWrapper>
                    <Calendar
                      className={classes.calendarDetail}
                      calendarType={"US"}
                      value={new Date(appointment.scheduledAt)}
                      tileDisabled={({ date }) => date.getDay() === 6} //disabled on saturdays
                    />
                  </CalendarWrapper>
                </div>
              </div>
            </div>
            <div className={classes.buttonDiv}>
              <SendEmailModal name={client?.firstName} email={client?.email} />
              {client?.street && (
                <Btn
                  className={classes.buttonDetail}
                  onClick={() =>
                    window.open(
                      `https://www.zillow.com/homes/${client.street}, ${client.city}, ${client.state} ${client.zip}`
                    )
                  }
                >
                  <img src={ZillowIcon} alt="zillow" />
                  <Typography style={{ marginLeft: "5%" }}>Zillow</Typography>
                </Btn>
              )}
            </div>
          </Box>
        </Modal>
      )}
    </div>
  );
};

export default AppointmentDetailModal;
